import { ThumbsUp, ThumbsDown, Mail, ExternalLink, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { STATUSES } from "@/lib/scoutApi";
import { copyText as copyToClipboard } from "@/lib/clipboard";
import { ResearchStatus } from "@/components/scout/ResearchStatus";

const Stars = ({ n }) => (
  <span className="font-mono text-xs text-[var(--accent)]" title={`Reachability ${n}/5`}>
    {"★".repeat(n)}
    <span className="text-slate-300">{"★".repeat(5 - n)}</span>
  </span>
);

const mailto = (c) => {
  const subject = encodeURIComponent(`ATFQ Podcast — an invitation, ${c.name.split(" ")[0]}`);
  return `mailto:${c.contact_email || ""}?subject=${subject}&body=${encodeURIComponent(c.outreach_draft || "")}`;
};

export const CandidateHeader = ({ c, onChange }) => (
  <div>
    <h3 className="font-display text-2xl text-[#0A192F] tracking-tight" data-testid="scout-candidate-name">{c.name}</h3>
    <p className="text-sm text-slate-600">
      {c.role}{c.affiliation ? ` · ${c.affiliation}` : ""}{c.location ? ` · ${c.location}` : ""}
    </p>
    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
      {c.pillars.map((p) => <span key={p} className="border border-slate-300 px-2 py-0.5">{p}</span>)}
      {c.adversarial && (
        <span data-testid="scout-candidate-adversarial" className="border border-[#0A192F] bg-[#0A192F] text-white px-2 py-0.5">Adversarial</span>
      )}
      {c.lane === "left" && <span data-testid="scout-candidate-lane" className="border border-rose-300 bg-rose-50 text-rose-800 px-2 py-0.5">Left</span>}
      {c.lane === "science" && <span data-testid="scout-candidate-lane" className="border border-sky-300 bg-sky-50 text-sky-800 px-2 py-0.5">Science</span>}
      <Stars n={c.reachability_score} />
      <span>{c.reachability_notes}</span>
    </div>
    {c.stance && <p data-testid="scout-candidate-stance" className="mt-2 text-xs text-[#0A192F] italic">{c.stance}</p>}
    <ResearchStatus c={c} onChange={onChange} />
  </div>
);

const fbCls = (active, dark) =>
  `p-2 border transition-colors ${active ? (dark ? "bg-[#0A192F] border-[#0A192F] text-white" : "bg-[var(--accent)] border-[var(--accent)] text-white") : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`;

export const CandidateActions = ({ c, onStatus, onFeedback, onRemove }) => (
  <div className="flex items-center gap-2">
    <select data-testid="scout-candidate-status" value={c.status} onChange={(e) => onStatus(e.target.value)}
      className="border border-slate-300 bg-white px-3 py-2 text-xs uppercase tracking-wider outline-none">
      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
    <button type="button" data-testid="scout-candidate-thumbs-up" onClick={() => onFeedback("up")} aria-pressed={c.feedback === "up"} className={fbCls(c.feedback === "up")}>
      <ThumbsUp className="w-4 h-4" />
    </button>
    <button type="button" data-testid="scout-candidate-thumbs-down" onClick={() => onFeedback("down")} aria-pressed={c.feedback === "down"} className={fbCls(c.feedback === "down", true)}>
      <ThumbsDown className="w-4 h-4" />
    </button>
    <button type="button" data-testid="scout-candidate-delete" onClick={onRemove} aria-label="Remove candidate"
      className="p-2 border border-slate-300 text-slate-400 hover:text-red-600 hover:border-red-300 transition-colors">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

export const CandidateFacts = ({ c }) => (
  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
    <div>
      <div className="text-xs text-slate-500 mb-1">Contact route</div>
      <div className="text-[#0A192F]">{c.contact_route || "—"}</div>
      {c.contact_email && <div className="text-[var(--accent)]">{c.contact_email}</div>}
      {c.contact_url && (
        <a href={c.contact_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline">
          Contact page <ExternalLink className="w-3 h-3" />
        </a>
      )}
      <div className="mt-3 text-xs text-slate-500 mb-1">Sources</div>
      <ul className="space-y-1">
        {c.sources.map((s) => (
          <li key={s.url}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-[var(--accent)] break-all">
              {s.title || s.url} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
    <div>
      <div className="text-xs text-slate-500 mb-1">Suggested ATFQ questions</div>
      <ol className="list-decimal pl-5 space-y-1 text-slate-700">{c.suggested_questions.map((q) => <li key={q}>{q}</li>)}</ol>
    </div>
  </div>
);

export const OutreachBlock = ({ c, busy, onDraft }) => (
  <div className="mt-5">
    {c.outreach_draft ? (
      <div data-testid="scout-outreach-draft" className="border border-slate-200 bg-slate-50 p-4">
        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{c.outreach_draft}</pre>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={mailto(c)} data-testid="scout-outreach-mailto" className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[var(--accent)] text-white px-4 py-2 text-xs font-medium transition-colors">
            <Mail className="w-3.5 h-3.5" /> Open in email
          </a>
          <button type="button" onClick={() => copyToClipboard(c.outreach_draft).then(() => toast.success("Copied"))} className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] px-4 py-2 text-xs font-medium transition-colors">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button type="button" onClick={onDraft} disabled={busy} className="text-xs text-slate-500 hover:text-[#0A192F] px-2">
            {busy ? "Redrafting…" : "Redraft"}
          </button>
        </div>
      </div>
    ) : (
      <button type="button" data-testid="scout-outreach-generate" onClick={onDraft} disabled={busy}
        className="inline-flex items-center gap-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white disabled:opacity-50 px-4 py-2 text-xs font-medium transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
        {busy ? "Drafting in Vic's voice…" : "Draft outreach email"}
      </button>
    )}
  </div>
);
